import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  Card,
  CardContent,
  Divider,
  IconButton,
  InputAdornment,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Snackbar,
  Alert,
  CircularProgress,
  Chip,
  List,
  ListItem,
  ListItemText,
  Avatar,
  Checkbox,
  FormControlLabel,
  Tabs,
  Tab,
  AppBar,
  Toolbar,
  MenuItem,
  Slider,
  Stepper,
  Step,
  StepLabel
} from '@mui/material';
import {
  Search as SearchIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Print as PrintIcon,
  AddAPhoto as AddAPhotoIcon,
  DirectionsBoat as BoatIcon,
  ArrowBack as CheckOutIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { format, parseISO, isToday, parse, differenceInMinutes } from 'date-fns';
import {
  getAllReservations,
  getReservationById,
  updateReservation
} from '../../firebase/services/reservationService';
import { formatPhoneNumber, formatTime12Hour } from '../../utils/formatters';
import { useNavigate, useSearchParams } from 'react-router-dom';
import MultiPaymentForm from '../../components/MultiPaymentForm';
import { payments } from '@square/web-sdk';
import { getAvailableVesselsForDate } from '../../firebase/services/vesselService';
import { uploadImage, IMAGE_CATEGORIES } from '../../firebase/services/imageService';

// Define TabPanel component
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`checkin-tabpanel-${index}`}
      aria-labelledby={`checkin-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 2 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `checkin-tab-${index}`,
    'aria-controls': `checkin-tabpanel-${index}`,
  };
}

// Helper: Title case
function toTitleCase(str: string) {
  return str.replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase());
}

const CheckInPage: React.FC = () => {
  const { userProfile } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // State for tabs
  const [tabValue, setTabValue] = useState(() => {
    const tab = searchParams.get('tab');
    return tab === '1' ? 1 : 0;
  });
  
  // State for data
  const [todayReservations, setTodayReservations] = useState<any[]>([]);
  const [checkedInReservations, setCheckedInReservations] = useState<any[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredReservations, setFilteredReservations] = useState<any[]>([]);
  const [filteredCheckedInReservations, setFilteredCheckedInReservations] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // State for dialogs
  const [selectedReservation, setSelectedReservation] = useState<any>(null);
  const [checkInDialogOpen, setCheckInDialogOpen] = useState(false);
  const [checkInNotes, setCheckInNotes] = useState('');
  const [boatInspected, setBoatInspected] = useState(false);
  const [safetyEquipmentVerified, setSafetyEquipmentVerified] = useState(false);
  const [driverLicenseVerified, setDriverLicenseVerified] = useState(false);
  const [waiverSigned, setWaiverSigned] = useState(false);
  const [checkOutDialogOpen, setCheckOutDialogOpen] = useState(false);
  const [checkOutNotes, setCheckOutNotes] = useState('');
  
  // State for notifications
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error' | 'info' | 'warning'>('success');
  
  // Add additional state variables for payment
  const [remainingBalance, setRemainingBalance] = useState<number>(0);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [paymentComplete, setPaymentComplete] = useState(false);
  const [paymentId, setPaymentId] = useState<string | null>(null);
  const [paymentProcessingError, setPaymentProcessingError] = useState<string | null>(null);
  
  // Square Web Payments SDK
  const [squarePayments, setSquarePayments] = useState<any>(null);
  const [cardReaderReady, setCardReaderReady] = useState(false);
  
  // Add state for auth code
  const [authCode, setAuthCode] = useState('');
  
  // Add state for vessel assignment, fuel, and hour meter
  const [availableVessels, setAvailableVessels] = useState<any[]>([]);
  const [assignedVesselId, setAssignedVesselId] = useState('');
  const [fuelLevel, setFuelLevel] = useState(4); // 0-4 (Empty, 1/4, 1/2, 3/4, Full)
  const [hourMeterStart, setHourMeterStart] = useState('');
  
  // Add stepper state
  const steps = [
    'Reservation Details',
    'Vessel & Driver Info',
    'Checklist & Boat Condition',
  ];
  const [activeStep, setActiveStep] = useState(0);

  // Helper: Check if driver's license photo is attached
  const hasLicensePhoto = !!selectedReservation?.driverLicensePhotoUrl;
  const [driverLicensePhoto, setDriverLicensePhoto] = useState<string | null>(null);

  const [uploadingLicensePhoto, setUploadingLicensePhoto] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  // Add state for return fuel and hour meter
  const [returnFuelLevel, setReturnFuelLevel] = useState<number | null>(null);
  const [returnHourMeter, setReturnHourMeter] = useState<string>('');

  // Add state for fuel gallons used and damage photo
  const [fuelGallonsUsed, setFuelGallonsUsed] = useState<string>('');
  const [damagePhoto, setDamagePhoto] = useState<string | null>(null);
  const [uploadingDamagePhoto, setUploadingDamagePhoto] = useState(false);
  const [damagePhotoUrl, setDamagePhotoUrl] = useState<string | null>(null);

  const handleNext = () => setActiveStep((prev) => prev + 1);
  const handleBack = () => setActiveStep((prev) => prev - 1);
  const handleStepReset = () => setActiveStep(0);
  
  // Fetch today's reservations
  useEffect(() => {
    const fetchReservations = async () => {
      try {
        setLoading(true);
        const allReservations = await getAllReservations();
        
        // Filter for today's reservations that are confirmed and not checked-in
        const today = format(new Date(), 'yyyy-MM-dd');
        const todaysReservations = allReservations.filter(res => {
          // Skip completely invalid reservations
          if (!res) return false;
          
          try {
            // If no date, allow it if status is confirmed (might be a test reservation)
            if (!res.date) {
              return res.status === 'confirmed' && 
                    res.checkInStatus !== 'checked-in' &&
                    res.checkInStatus !== 'checked-out';
            }
            
            const resDate = format(parseISO(res.date), 'yyyy-MM-dd');
            return resDate === today && 
                   res.status === 'confirmed' && 
                   res.checkInStatus !== 'checked-in' &&
                   res.checkInStatus !== 'checked-out';
          } catch (err) {
            // Include reservations with date parsing errors (might be today)
            return res.status === 'confirmed' && 
                   res.checkInStatus !== 'checked-in' &&
                   res.checkInStatus !== 'checked-out';
          }
        });
        
        // Filter for already checked-in reservations that aren't checked-out yet
        const checkedIn = allReservations.filter(res => {
          return res.checkInStatus === 'checked-in';
        });
        
        setTodayReservations(todaysReservations);
        setCheckedInReservations(checkedIn);
        setFilteredReservations(todaysReservations);
        setFilteredCheckedInReservations(checkedIn);
      } catch (err) {
        setError('Failed to load today\'s reservations. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchReservations();
    
    // Refresh reservations every 5 minutes
    const refreshInterval = setInterval(fetchReservations, 5 * 60 * 1000);
    
    return () => clearInterval(refreshInterval);
  }, []);
  
  // Handle tab change
  const handleTabChange = async (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    // Update URL with new tab value
    navigate(`/staff/checkin?tab=${newValue}`, { replace: true });
    try {
      setLoading(true);
      const allReservations = await getAllReservations();
      
      // Filter for today's reservations that are confirmed and not checked-in
      const today = format(new Date(), 'yyyy-MM-dd');
      const todaysReservations = allReservations.filter(res => {
        // Skip completely invalid reservations
        if (!res) return false;
        
        try {
          // If no date, allow it if status is confirmed (might be a test reservation)
          if (!res.date) {
            return res.status === 'confirmed' && 
                  res.checkInStatus !== 'checked-in' &&
                  res.checkInStatus !== 'checked-out';
          }
          
          const resDate = format(parseISO(res.date), 'yyyy-MM-dd');
          return resDate === today && 
                 res.status === 'confirmed' && 
                 res.checkInStatus !== 'checked-in' &&
                 res.checkInStatus !== 'checked-out';
        } catch (err) {
          // Include reservations with date parsing errors (might be today)
          return res.status === 'confirmed' && 
                 res.checkInStatus !== 'checked-in' &&
                 res.checkInStatus !== 'checked-out';
        }
      });
      
      // Filter for already checked-in reservations that aren't checked-out yet
      const checkedIn = allReservations.filter(res => {
        return res.checkInStatus === 'checked-in';
      });
      
      setTodayReservations(todaysReservations);
      setCheckedInReservations(checkedIn);
      setFilteredReservations(todaysReservations);
      setFilteredCheckedInReservations(checkedIn);
    } catch (err) {
      setError('Failed to refresh reservations. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  // Handle search
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredReservations(todayReservations);
      setFilteredCheckedInReservations(checkedInReservations);
      return;
    }
    
    const searchTermLower = searchTerm.toLowerCase();
    
    // Filter upcoming reservations
    const filtered = todayReservations.filter(res => {
      const firstName = res.customerDetails?.firstName?.toLowerCase() || '';
      const lastName = res.customerDetails?.lastName?.toLowerCase() || '';
      const fullName = `${firstName} ${lastName}`;
      const email = res.customerDetails?.email?.toLowerCase() || '';
      const phone = res.customerDetails?.phone || '';
      const reservationId = res.id?.toLowerCase() || '';
      const vesselName = res.vesselName?.toLowerCase() || '';
      
      return fullName.includes(searchTermLower) ||
             email.includes(searchTermLower) ||
             phone.includes(searchTermLower) ||
             reservationId.includes(searchTermLower) ||
             vesselName.includes(searchTermLower);
    });
    
    // Filter checked-in reservations
    const filteredCheckedIn = checkedInReservations.filter(res => {
      const firstName = res.customerDetails?.firstName?.toLowerCase() || '';
      const lastName = res.customerDetails?.lastName?.toLowerCase() || '';
      const fullName = `${firstName} ${lastName}`;
      const email = res.customerDetails?.email?.toLowerCase() || '';
      const phone = res.customerDetails?.phone || '';
      const reservationId = res.id?.toLowerCase() || '';
      const vesselName = res.vesselName?.toLowerCase() || '';
      
      return fullName.includes(searchTermLower) ||
             email.includes(searchTermLower) ||
             phone.includes(searchTermLower) ||
             reservationId.includes(searchTermLower) ||
             vesselName.includes(searchTermLower);
    });
    
    setFilteredReservations(filtered);
    setFilteredCheckedInReservations(filteredCheckedIn);
  }, [searchTerm, todayReservations, checkedInReservations]);
  
  // Open check-in dialog
  const handleOpenCheckInDialog = (reservation: any) => {
    setSelectedReservation(reservation);
    setCheckInNotes('');
    setBoatInspected(false);
    setSafetyEquipmentVerified(false);
    setDriverLicenseVerified(false);
    setWaiverSigned(false);
    setCheckInDialogOpen(true);
  };
  
  // Fetch available vessels when check-in dialog opens
  useEffect(() => {
    if (checkInDialogOpen && selectedReservation) {
      const fetchVessels = async () => {
        try {
          const vessels = await getAvailableVesselsForDate(
            selectedReservation.vesselType,
            selectedReservation.date,
            selectedReservation.timeSlot
          );
          setAvailableVessels(vessels);
        } catch (err) {
          setAvailableVessels([]);
        }
      };
      fetchVessels();
      setAssignedVesselId('');
      setFuelLevel(4);
      setHourMeterStart('');
    }
  }, [checkInDialogOpen, selectedReservation]);
  
  // Complete check-in process
  const handleConfirmCheckIn = async () => {
    if (!selectedReservation) return;
    
    // Ensure reservation ID exists
    if (!selectedReservation.id) {
      setSnackbarMessage('Error: Invalid reservation ID');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }
    
    // Validation
    if (!boatInspected || !safetyEquipmentVerified || !driverLicenseVerified || !waiverSigned) {
      setSnackbarMessage('Please complete all required check-in items');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }
    
    try {
      setLoading(true);
      setUploadError(null);
      let driverLicensePhotoUrl = selectedReservation.driverLicensePhotoUrl || null;
      // If a new license photo was uploaded, persist it
      if (driverLicensePhoto && !selectedReservation.driverLicensePhotoUrl) {
        setUploadingLicensePhoto(true);
        try {
          // Convert base64 to File
          const res = await fetch(driverLicensePhoto);
          const blob = await res.blob();
          const file = new File([blob], `license-${selectedReservation.id}.jpg`, { type: blob.type });
          // Use the 'ID' category for uploadImage (not the path string)
          const imageMeta = await uploadImage(file, 'ID', {
            title: `Driver License for reservation ${selectedReservation.id}`,
            altText: `${selectedReservation.customerDetails.firstName} ${selectedReservation.customerDetails.lastName} License`,
          });
          driverLicensePhotoUrl = imageMeta.storageUrl;
        } catch (err: any) {
          setUploadError('Failed to upload license photo.');
          setLoading(false);
          setUploadingLicensePhoto(false);
          return;
        } finally {
          setUploadingLicensePhoto(false);
        }
      }
      const checkInData = {
        checkInStatus: 'checked-in' as const,
        checkInTime: new Date().toISOString(),
        checkInNotes: checkInNotes,
        checkInBy: userProfile?.id || 'staff',
        checkInDetails: {
          boatInspected,
          safetyEquipmentVerified,
          driverLicenseVerified,
          waiverSigned,
          staffName: userProfile?.email || 'Staff Member',
        },
        assignedVesselId,
        assignedVesselName: availableVessels.find(v => v.id === assignedVesselId)?.name || '',
        startingFuelLevel: fuelLevel,
        hourMeterStart: hourMeterStart ? Number(hourMeterStart) : null,
        driverLicensePhotoUrl, // Save the URL if available
      };
      
      await updateReservation(selectedReservation.id, checkInData);
      
      // Update local state
      setTodayReservations(prevReservations => 
        prevReservations.filter(res => res.id !== selectedReservation.id)
      );
      
      setFilteredReservations(prevFiltered => 
        prevFiltered.filter(res => res.id !== selectedReservation.id)
      );
      
      setSnackbarMessage(`${selectedReservation.customerDetails.firstName} ${selectedReservation.customerDetails.lastName} checked in successfully`);
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      
      setCheckInDialogOpen(false);
    } catch (err) {
      setSnackbarMessage(`Failed to check in: ${err instanceof Error ? err.message : 'Unknown error'}`);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };
  
  // Print check-in receipt
  const handlePrintReceipt = () => {
    if (!selectedReservation) return;
    
    // Create and open a print window
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      setSnackbarMessage('Pop-up blocked. Please allow pop-ups to print receipts.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }
    
    // Format reservation data for receipt
    const customer = selectedReservation.customerDetails;
    const reservationDate = selectedReservation.date ? format(parseISO(selectedReservation.date), 'EEEE, MMMM d, yyyy') : 'N/A';
    const startTime = formatTime12Hour(selectedReservation.startTime);
    const endTime = formatTime12Hour(selectedReservation.endTime);
    
    // Create receipt HTML
    printWindow.document.write(`
      <html>
        <head>
          <title>Check-In Receipt - Fallen Leaf Marina</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 0; padding: 20px; }
            .receipt { max-width: 80mm; margin: 0 auto; }
            .header { text-align: center; margin-bottom: 20px; }
            .logo { font-size: 24px; font-weight: bold; margin-bottom: 5px; }
            .info { margin-bottom: 15px; }
            .info-row { display: flex; margin-bottom: 5px; }
            .info-label { font-weight: bold; width: 150px; }
            .info-value { flex: 1; }
            .divider { border-top: 1px dashed #ccc; margin: 15px 0; }
            .footer { text-align: center; font-size: 12px; margin-top: 20px; }
            @media print {
              body { width: 80mm; }
            }
          </style>
        </head>
        <body>
          <div class="receipt">
            <div class="header">
              <div class="logo">Fallen Leaf Marina</div>
              <div>Check-In Receipt</div>
            </div>
            
            <div class="info">
              <div class="info-row">
                <div class="info-label">Reservation ID:</div>
                <div class="info-value">${selectedReservation.id ? selectedReservation.id.substring(0, 8) : 'N/A'}</div>
              </div>
              <div class="info-row">
                <div class="info-label">Date:</div>
                <div class="info-value">${reservationDate}</div>
              </div>
              <div class="info-row">
                <div class="info-label">Time:</div>
                <div class="info-value">${startTime} - ${endTime}</div>
              </div>
            </div>
            
            <div class="divider"></div>
            
            <div class="info">
              <div class="info-row">
                <div class="info-label">Customer:</div>
                <div class="info-value">${customer.firstName} ${customer.lastName}</div>
              </div>
              <div class="info-row">
                <div class="info-label">Email:</div>
                <div class="info-value">${customer.email}</div>
              </div>
              <div class="info-row">
                <div class="info-label">Phone:</div>
                <div class="info-value">${formatPhoneNumber(customer.phone)}</div>
              </div>
            </div>
            
            <div class="divider"></div>
            
            <div class="info">
              <div class="info-row">
                <div class="info-label">Vessel:</div>
                <div class="info-value">${selectedReservation.vesselName}</div>
              </div>
              <div class="info-row">
                <div class="info-label">Vessel Out Time:</div>
                <div class="info-value">${format(new Date(), 'hh:mm a')}</div>
              </div>
              <div class="info-row">
                <div class="info-label">Staff:</div>
                <div class="info-value">${userProfile?.email || 'Staff Member'}</div>
              </div>
            </div>
            
            <div class="divider"></div>
            
            <div class="footer">
              <p>Thank you for visiting Fallen Leaf Marina!</p>
              <p>Please return the vessel by ${endTime}</p>
            </div>
          </div>
          <script>
            window.onload = function() {
              window.print();
              setTimeout(function() { window.close(); }, 500);
            };
          </script>
        </body>
      </html>
    `);
    
    printWindow.document.close();
  };
  
  // Render each reservation card
  const renderReservationCard = (reservation: any, isCheckOut: boolean = false) => {
    // Create a safe version of the reservation data with defaults
    const safeReservation = {
      id: reservation?.id || 'unknown-id',
      vesselName: reservation?.vesselName || 'Unnamed Vessel',
      startTime: reservation?.startTime || 'N/A',
      endTime: reservation?.endTime || 'N/A',
      customerDetails: {
        firstName: reservation?.customerDetails?.firstName || 'Guest',
        lastName: reservation?.customerDetails?.lastName || '',
        email: reservation?.customerDetails?.email || 'No email provided',
        phone: reservation?.customerDetails?.phone || 'No phone provided'
      },
      paymentStatus: reservation?.paymentStatus || 'unpaid',
      additionalNotes: reservation?.additionalNotes || '',
      checkInTime: reservation?.checkInTime || null
    };
    
    const customer = safeReservation.customerDetails;
    const reservationTime = `${formatTime12Hour(safeReservation.startTime)} - ${formatTime12Hour(safeReservation.endTime)}`;
    
    const vesselDisplay = reservation.vesselName && reservation.vesselName.trim() !== ''
      ? reservation.vesselName
      : (reservation.vesselType ? toTitleCase(reservation.vesselType) : 'Vessel');

    return (
      <Card 
        key={safeReservation.id} 
        sx={{ 
          mb: 2, 
          display: 'flex', 
          flexDirection: { xs: 'column', sm: 'row' },
          position: 'relative',
          transition: 'transform 0.2s', 
          '&:hover': { 
            transform: 'scale(1.01)', 
            boxShadow: 3 
          },
          // If this is a check-out card, add a subtle indicator
          borderLeft: isCheckOut ? '4px solid #4caf50' : 'none'
        }}
      >
        <Box sx={{ 
          backgroundColor: isCheckOut ? 'success.main' : 'primary.main',
          color: 'primary.contrastText',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          padding: 2,
          width: { xs: '100%', sm: '25%' },
          minWidth: { sm: 180 }
        }}>
          <Avatar 
            sx={{ 
              bgcolor: 'white', 
              color: isCheckOut ? 'success.main' : 'primary.main', 
              width: 56, 
              height: 56,
              mb: 1
            }}
          >
            <BoatIcon fontSize="large" />
          </Avatar>
          <Typography variant="h6" align="center">
            {vesselDisplay}
          </Typography>
          <Typography variant="body2" align="center" sx={{ mt: 1 }}>
            {reservationTime}
          </Typography>
          <Typography variant="caption" align="center" display="block">
            {(() => {
              const start = reservation.startTime;
              const end = reservation.endTime;
              if (start && end) {
                try {
                  const startDate = parse(start, 'hh:mm a', new Date());
                  const endDate = parse(end, 'hh:mm a', new Date());
                  let diff = differenceInMinutes(endDate, startDate);
                  if (diff < 0) diff += 24 * 60;
                  const hours = Math.floor(diff / 60);
                  const mins = diff % 60;
                  const hourStr = hours === 1 ? '1 Hour' : `${hours} Hours`;
                  const minStr = mins === 0 ? '' : (mins === 1 ? '1 Minute' : `${mins} Minutes`);
                  return minStr ? `${hourStr} ${minStr}` : hourStr;
                } catch {
                  return '';
                }
              }
              return '';
            })()}
          </Typography>
          {isCheckOut && safeReservation.checkInTime && (
            <Typography variant="caption" align="center" sx={{ mt: 0.5 }}>
              Checked in: {(() => {
                try {
                  if (safeReservation.checkInTime?.toDate) {
                    // Firebase Timestamp
                    return format(safeReservation.checkInTime.toDate(), 'h:mm a');
                  } else if (typeof safeReservation.checkInTime === 'string') {
                    // ISO string
                    return format(parseISO(safeReservation.checkInTime), 'h:mm a');
                  }
                  return 'N/A';
                } catch {
                  return 'N/A';
                }
              })()}
            </Typography>
          )}
        </Box>
        <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
          <Box>
            <Typography variant="h6" component="h2">
              {customer.firstName} {customer.lastName}
            </Typography>
            <Typography color="text.secondary" gutterBottom>
              {formatPhoneNumber(customer.phone)} | {customer.email}
            </Typography>
            
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  Reservation ID: <b>{reservation.id ? reservation.id.substring(0, 8) : 'N/A'}</b>
                </Typography>
              </Grid>
              <Grid xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  {reservation.payment && reservation.payment.depositPaid && reservation.payment.remainingAmount > 0 ? (
                    <Chip label="Deposit Paid" color="info" size="small" sx={{ ml: 1 }} />
                  ) : reservation.payment && reservation.payment.remainingAmount <= 0 ? (
                    <Chip label="Paid" color="success" size="small" sx={{ ml: 1 }} />
                  ) : (
                    <Chip label={reservation.paymentStatus || 'unpaid'} color="warning" size="small" sx={{ ml: 1 }} />
                  )}
                </Box>
              </Grid>
              {safeReservation.additionalNotes && (
                <Grid xs={12}>
                  <Typography variant="body2">
                    <b>Notes:</b> {safeReservation.additionalNotes}
                  </Typography>
                </Grid>
              )}
            </Grid>
          </Box>
          
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', pt: 2 }}>
            <Button
              variant="contained"
              color={isCheckOut ? "success" : "primary"}
              onClick={() => isCheckOut ? handleOpenCheckOutDialog(reservation) : handleOpenCheckInDialog(reservation)}
              startIcon={isCheckOut ? <CheckOutIcon /> : <CheckCircleIcon />}
              size="large"
            >
              {isCheckOut ? 'Check Out' : 'Check In'}
            </Button>
          </Box>
        </CardContent>
      </Card>
    );
  };
  
  // Calculate remaining balance when a reservation is selected for check-out
  const handleOpenCheckOutDialog = (reservation: any) => {
    setSelectedReservation(reservation);
    setCheckOutNotes('');
    setReturnFuelLevel(null);
    setReturnHourMeter('');
    setFuelGallonsUsed('');
    setDamagePhoto(null);
    setDamagePhotoUrl(null);
    
    // Calculate remaining balance
    let balance = 0;
    
    // Get the total price and any existing payments
    const totalPrice = parseFloat(reservation.totalPrice || '0');
    const depositAmount = parseFloat(reservation.depositAmount || '0');
    const finalPaymentAmount = parseFloat(reservation.finalPaymentAmount || '0');
    
    // Calculate remaining balance based on payment status
    if (reservation.paymentStatus === 'deposit_paid') {
      // If only deposit is paid, remaining balance is (total - deposit)
      balance = totalPrice - depositAmount;
    } else if (reservation.paymentStatus === 'partial') {
      // If partial payment was made, remaining balance is (total - deposit - finalPayment)
      balance = totalPrice - depositAmount - finalPaymentAmount;
    } else if (reservation.paymentStatus !== 'paid') {
      // If no payment or other status, remaining balance is the full amount
      balance = totalPrice;
    }
    
    setRemainingBalance(balance);
    setShowPaymentForm(balance > 0);
    setPaymentComplete(balance === 0);
    setCheckOutDialogOpen(true);
  };
  
  // Handle payment completion
  const handlePaymentComplete = (paymentId: string, status: string, processor: string) => {
    setPaymentId(paymentId);
    setPaymentComplete(true);
    setPaymentProcessingError(null);
    
    // Update snackbar with success message
    setSnackbarMessage(`Payment processed successfully. ID: ${paymentId}`);
    setSnackbarSeverity('success');
    setSnackbarOpen(true);
  };
  
  // Handle payment error
  const handlePaymentError = (error: string) => {
    setPaymentProcessingError(error);
    
    // Show error message
    setSnackbarMessage(`Payment failed: ${error}`);
    setSnackbarSeverity('error');
    setSnackbarOpen(true);
  };
  
  // Modify the handleConfirmCheckOut function to update payment info
  const handleConfirmCheckOut = async () => {
    if (!selectedReservation) return;
    if (!authCode) {
      setSnackbarMessage('Payment authorization code is required');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }
    if (fuelGallonsUsed === '') {
      setSnackbarMessage('Please enter fuel gallons used');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }
    let uploadedDamagePhotoUrl = null;
    if (damagePhoto && !damagePhotoUrl) {
      setUploadingDamagePhoto(true);
      try {
        const res = await fetch(damagePhoto);
        const blob = await res.blob();
        const file = new File([blob], `damage-${selectedReservation.id}.jpg`, { type: blob.type });
        const imageMeta = await uploadImage(file, 'ID', {
          title: `Damage Photo for reservation ${selectedReservation.id}`,
          altText: `Damage Photo for ${selectedReservation.customerDetails.firstName} ${selectedReservation.customerDetails.lastName}`,
        });
        uploadedDamagePhotoUrl = imageMeta.storageUrl;
        setDamagePhotoUrl(uploadedDamagePhotoUrl);
      } catch (err) {
        setSnackbarMessage('Failed to upload damage photo.');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
        setUploadingDamagePhoto(false);
        return;
      } finally {
        setUploadingDamagePhoto(false);
      }
    }
    
    try {
      setLoading(true);
      
      const checkOutData: any = {
        checkInStatus: 'checked-out' as const,
        checkOutTime: new Date().toISOString(),
        checkOutNotes: checkOutNotes,
        checkOutBy: userProfile?.id || 'staff',
        paymentAuthCode: authCode,
        fuelGallonsUsed: fuelGallonsUsed ? Number(fuelGallonsUsed) : null,
        returnHourMeter: returnHourMeter ? Number(returnHourMeter) : null,
        damagePhotoUrl: uploadedDamagePhotoUrl || damagePhotoUrl || null,
      };
      
      // If payment was completed, update payment status
      if (paymentComplete && paymentId) {
        checkOutData.paymentStatus = 'paid';
        checkOutData.finalPaymentId = paymentId;
        checkOutData.finalPaymentDate = new Date().toISOString();
      }
      
      await updateReservation(selectedReservation.id, checkOutData);
      
      // Update local state
      setCheckedInReservations(prevReservations => 
        prevReservations.filter(res => res.id !== selectedReservation.id)
      );
      
      setFilteredCheckedInReservations(prevFiltered => 
        prevFiltered.filter(res => res.id !== selectedReservation.id)
      );
      
      setSnackbarMessage(`${selectedReservation.customerDetails.firstName} ${selectedReservation.customerDetails.lastName} checked out successfully`);
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      
      setCheckOutDialogOpen(false);
    } catch (err) {
      setSnackbarMessage(`Failed to check out: ${err instanceof Error ? err.message : 'Unknown error'}`);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };
  
  // Initialize Square Web SDK
  useEffect(() => {
    const initializeSquare = async () => {
      try {
        const paymentsInstance = await payments(
          process.env.REACT_APP_SQUARE_APP_ID || '',
          process.env.REACT_APP_SQUARE_LOCATION_ID || ''
        );
        setSquarePayments(paymentsInstance);
        
        // Check if card reader is available
        try {
          const reader = await paymentsInstance.card();
          if (reader) {
            setCardReaderReady(true);
          }
        } catch (readerError) {
          setCardReaderReady(false);
        }
      } catch (error) {
        setError('Failed to initialize payment system');
      }
    };

    initializeSquare();
  }, []);

  // Handle card reader payment
  const handleCardReaderPayment = async (amount: number) => {
    if (!squarePayments || !cardReaderReady) {
      setSnackbarMessage('Card reader not available');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    try {
      setLoading(true);
      const card = await squarePayments.card();
      
      // Start card payment
      const result = await card.tokenize({
        amount: {
          amount: Math.round(amount * 100), // Convert to cents
          currency: 'USD'
        },
        note: `Check-out payment for reservation ${selectedReservation?.id}`,
        collectSignature: true,
        skipReceipt: false
      });

      if (result.status === 'OK') {
        // Payment successful
        setPaymentId(result.token);
        setPaymentComplete(true);
        setSnackbarMessage('Payment processed successfully');
        setSnackbarSeverity('success');
      } else {
        // Payment failed
        setSnackbarMessage(`Payment failed: ${result.errors?.[0]?.message || 'Unknown error'}`);
        setSnackbarSeverity('error');
      }
    } catch (error) {
      setSnackbarMessage('Failed to process payment');
      setSnackbarSeverity('error');
    } finally {
      setLoading(false);
      setSnackbarOpen(true);
    }
  };

  // Modify the payment form section in the check-out dialog
  const renderPaymentSection = () => {
    if (!showPaymentForm || paymentComplete) return null;

    // Calculate values for breakdown
    const totalPrice = parseFloat(selectedReservation?.totalPrice || '0');
    const depositAmount = parseFloat(selectedReservation?.depositAmount || '0');
    const remaining = remainingBalance;

    return (
      <Grid xs={12} md={6}>
        <Typography variant="subtitle1" gutterBottom>
          Payment - Remaining Balance
        </Typography>
        <Paper sx={{ p: 1.5, mb: 1 }}>
          <Box>
            <Typography variant="body1" gutterBottom>
              <b>Full Amount Due:</b> ${totalPrice.toFixed(2)}
            </Typography>
            <Typography variant="body1" gutterBottom>
              <b>Deposits Paid:</b> ${depositAmount.toFixed(2)}
            </Typography>
            <Typography variant="body1" gutterBottom>
              <b>Tax:</b> ${
                selectedReservation.taxAmount && !isNaN(Number(selectedReservation.taxAmount))
                  ? Number(selectedReservation.taxAmount).toFixed(2)
                  : selectedReservation.payment?.taxAmount && !isNaN(Number(selectedReservation.payment.taxAmount))
                    ? Number(selectedReservation.payment.taxAmount).toFixed(2)
                    : '0.00'
              }
            </Typography>
            <Typography variant="body1" gutterBottom>
              <b>Remaining Balance Due:</b> ${remaining.toFixed(2)}
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Please enter this amount into the Square Stand card reader and process the payment.
            </Typography>
            <TextField
              label="Credit Card Auth Code"
              value={authCode}
              onChange={e => setAuthCode(e.target.value)}
              fullWidth
              required
              sx={{ mt: 2 }}
              placeholder="Enter authorization code from Square Stand"
            />
            <Button
              variant="contained"
              color="success"
              fullWidth
              onClick={() => setPaymentComplete(true)}
              disabled={loading || !authCode}
              sx={{ mt: 2 }}
            >
              Payment Completed on Square Stand
            </Button>
          </Box>
        </Paper>
      </Grid>
    );
  };

  return (
    <Box sx={{ minHeight: '100vh', pb: 4 }}>
      <AppBar position="sticky" color="inherit" elevation={1} sx={{ mb: 2 }}>
        <Toolbar>
          <Typography variant="h5" component="h1" sx={{ flexGrow: 1 }}>
            Today's Check-Ins {filteredReservations.length > 0 ? `(${filteredReservations.length})` : ''}
          </Typography>
          <TextField
            placeholder="Search customers..."
            size="small"
            variant="outlined"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ width: { xs: '100%', sm: 300 }, mr: 2 }}
          />
          <Button 
            color="inherit" 
            onClick={() => navigate('/staff/reservations')}
          >
            Back to Reservations
          </Button>
        </Toolbar>
        
        <Box sx={{ width: '100%', bgcolor: 'background.paper' }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            indicatorColor="primary"
            textColor="primary"
            variant="fullWidth"
            aria-label="check-in tabs"
          >
            <Tab label="Upcoming" {...a11yProps(0)} />
            <Tab label="Recent" {...a11yProps(1)} />
          </Tabs>
        </Box>
      </AppBar>
      <Box sx={{ maxWidth: 1200, mx: 'auto', px: 2 }}>
        <TabPanel value={tabValue} index={0}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 5 }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          ) : filteredReservations.length > 0 ? (
            filteredReservations.map((reservation) => (
              <React.Fragment key={reservation?.id || Math.random().toString()}>
                {renderReservationCard(reservation)}
              </React.Fragment>
            ))
          ) : (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="h6" gutterBottom>
                No Upcoming Check-Ins
              </Typography>
              <Typography variant="body1" color="text.secondary">
                {searchTerm 
                  ? "No reservations match your search criteria" 
                  : "There are no reservations scheduled for check-in today"}
              </Typography>
            </Paper>
          )}
        </TabPanel>
        
        <TabPanel value={tabValue} index={1}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 5 }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          ) : filteredCheckedInReservations.length > 0 ? (
            filteredCheckedInReservations.map((reservation) => (
              <React.Fragment key={reservation?.id || Math.random().toString()}>
                {renderReservationCard(reservation, true)}
              </React.Fragment>
            ))
          ) : (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="h6" gutterBottom>
                No Active Check-Ins
              </Typography>
              <Typography variant="body1" color="text.secondary">
                {searchTerm 
                  ? "No checked-in reservations match your search criteria" 
                  : "There are no active check-ins at this time"}
              </Typography>
            </Paper>
          )}
        </TabPanel>
      </Box>
      {/* Check-in Dialog */}
      <Dialog 
        open={checkInDialogOpen} 
        onClose={() => !loading && setCheckInDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Check In: {selectedReservation?.customerDetails.firstName} {selectedReservation?.customerDetails.lastName}
        </DialogTitle>
        <DialogContent dividers>
          <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 3 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
          {selectedReservation && (
            <Grid container spacing={1}>
              {/* Step 1: Reservation Details & Notes */}
              {activeStep === 0 && (
                <Grid xs={12}>
                  {/* Customer Info at the top */}
                  <Typography variant="subtitle1" gutterBottom>
                    Customer Info
                  </Typography>
                  <Paper sx={{ p: 1.5, mb: 1 }}>
                    <Typography variant="body1" component="div">
                      <b>{selectedReservation.customerDetails.firstName} {selectedReservation.customerDetails.lastName}</b>
                    </Typography>
                    <Typography variant="body2" component="div">
                      {selectedReservation.customerDetails.email}
                    </Typography>
                    <Typography variant="body2" component="div">
                      {formatPhoneNumber(selectedReservation.customerDetails.phone)}
                    </Typography>
                  </Paper>
                  <Typography variant="subtitle1" gutterBottom>
                    Reservation Details
                  </Typography>
                  <Paper sx={{ p: 1.5, mb: 1 }}>
                    <List dense disablePadding>
                      <ListItem>
                        <Grid container spacing={1} alignItems="center">
                          <Grid xs={12}>
                            <Typography variant="subtitle2">Date & Time</Typography>
                            <Typography variant="body2">
                              {selectedReservation.date ? format(parseISO(selectedReservation.date), 'EEEE, MMMM d, yyyy') : 'N/A'} | 
                              {formatTime12Hour(selectedReservation.startTime)} - {formatTime12Hour(selectedReservation.endTime)}
                            </Typography>
                          </Grid>
                        </Grid>
                      </ListItem>
                      <Divider component="li" sx={{ my: 0.5 }} />
                      <ListItem>
                        <Grid container spacing={1}>
                          <Grid xs={12} sm={6}>
                            <Typography variant="subtitle2">Vessel Type Reserved</Typography>
                            <Typography variant="body2">
                              {selectedReservation.vesselType ? toTitleCase(selectedReservation.vesselType) : 'N/A'}
                            </Typography>
                          </Grid>
                          <Grid xs={12} sm={6}>
                            <Typography variant="subtitle2">Damage Waiver</Typography>
                            <Typography variant="body2">
                              {selectedReservation.damageWaiverAccepted === true ? 'Accepted' : selectedReservation.damageWaiverAccepted === false ? 'Declined' : 'N/A'}
                            </Typography>
                          </Grid>
                        </Grid>
                      </ListItem>
                      <Divider component="li" sx={{ my: 0.5 }} />
                      <ListItem>
                        <Grid container spacing={1}>
                          <Grid xs={12}>
                            <Typography variant="subtitle2">Payment Status</Typography>
                            <Box sx={{ mt: 0.5, display: 'flex', alignItems: 'center' }}>
                              {selectedReservation.payment && selectedReservation.payment.depositPaid && selectedReservation.payment.remainingAmount > 0 ? (
                                <>
                                  <Chip label="Deposit Paid" color="info" size="small" sx={{ mr: 1 }} />
                                  <Typography variant="body2">
                                    Remaining Due: ${selectedReservation.payment.remainingAmount.toFixed(2)}
                                  </Typography>
                                </>
                              ) : selectedReservation.payment && selectedReservation.payment.remainingAmount <= 0 ? (
                                <Chip label="Paid" color="success" size="small" />
                              ) : (
                                <Chip label={selectedReservation.paymentStatus || 'unpaid'} color="warning" size="small" />
                              )}
                            </Box>
                          </Grid>
                        </Grid>
                      </ListItem>
                      {/* Deposit details */}
                      {selectedReservation.payment && selectedReservation.payment.depositPaid && (
                        <ListItem>
                          <Grid container spacing={1}>
                            <Grid xs={4}>
                              <Typography variant="subtitle2">Deposit Amount Paid</Typography>
                              <Typography variant="body2">
                                ${selectedReservation.payment.depositAmount?.toFixed(2) || '0.00'}
                              </Typography>
                            </Grid>
                            <Grid xs={4}>
                              <Typography variant="subtitle2">How Paid</Typography>
                              <Typography variant="body2">
                                {selectedReservation.payment.paymentMethod ? toTitleCase(selectedReservation.payment.paymentMethod) : 'N/A'}
                                {selectedReservation.payment.processor ? ` (${toTitleCase(selectedReservation.payment.processor)})` : ''}
                              </Typography>
                            </Grid>
                            <Grid xs={4}>
                              <Typography variant="subtitle2">Deposit Paid On</Typography>
                              <Typography variant="body2">
                                {(() => {
                                  try {
                                    const paymentDate = selectedReservation.payment.paymentDate;
                                    if (paymentDate?.toDate) {
                                      // Firebase Timestamp
                                      return format(paymentDate.toDate(), 'PPpp');
                                    } else if (typeof paymentDate === 'string') {
                                      // ISO string
                                      return format(parseISO(paymentDate), 'PPpp');
                                    } else if (paymentDate) {
                                      // Regular date
                                      return format(new Date(paymentDate), 'PPpp');
                                    }
                                    return 'N/A';
                                  } catch {
                                    return 'N/A';
                                  }
                                })()}
                              </Typography>
                            </Grid>
                          </Grid>
                        </ListItem>
                      )}
                      {/* Reservation created at */}
                      {selectedReservation.createdAt && (
                        <ListItem>
                          <Grid container spacing={1}>
                            <Grid xs={12}>
                              <Typography variant="subtitle2">Reservation Made</Typography>
                              <Typography variant="body2">
                                {(() => {
                                  try {
                                    if (selectedReservation.createdAt?.toDate) {
                                      // Firebase Timestamp
                                      return format(selectedReservation.createdAt.toDate(), 'PPpp');
                                    } else if (selectedReservation.createdAt) {
                                      // Regular date
                                      return format(new Date(selectedReservation.createdAt), 'PPpp');
                                    }
                                    return 'N/A';
                                  } catch {
                                    return 'N/A';
                                  }
                                })()}
                              </Typography>
                            </Grid>
                          </Grid>
                        </ListItem>
                      )}
                    </List>
                  </Paper>
                </Grid>
              )}
              {/* Step 2: Vessel Assignment & Driver Data */}
              {activeStep === 1 && (
                <Grid xs={12}>
                  <Paper sx={{ p: 1.5, mb: 1 }}>
                    <Typography variant="subtitle1" gutterBottom>
                      Assign Vessel
                    </Typography>
                    <TextField
                      select
                      label="Select Vessel"
                      value={assignedVesselId}
                      onChange={e => setAssignedVesselId(e.target.value)}
                      fullWidth
                      required
                      sx={{ mb: 2 }}
                      disabled={availableVessels.length === 0}
                      helperText={availableVessels.length === 0 ? 'No available vessels of this type' : ''}
                    >
                      {availableVessels.map(vessel => (
                        <MenuItem key={vessel.id} value={vessel.id}>
                          {vessel.name} (#{vessel.registrationNumber})
                        </MenuItem>
                      ))}
                    </TextField>
                    <Divider sx={{ my: 0.5 }} />
                    <Typography variant="subtitle1" gutterBottom>
                      Driver's License
                    </Typography>
                    {hasLicensePhoto || driverLicensePhoto ? (
                      <Box sx={{ mb: 2 }}>
                        <img
                          src={hasLicensePhoto ? selectedReservation.driverLicensePhotoUrl : driverLicensePhoto!}
                          alt="Driver's License"
                          style={{ maxWidth: 240, borderRadius: 4, border: '1px solid #ccc' }}
                        />
                      </Box>
                    ) : (
                      <Button
                        variant="outlined"
                        startIcon={<AddAPhotoIcon />}
                        component="label"
                        sx={{ mb: 2 }}
                      >
                        Upload License Photo
                        <input
                          type="file"
                          accept="image/*"
                          hidden
                          onChange={e => {
                            const file = e.target.files?.[0];
                            if (file) {
                              const reader = new FileReader();
                              reader.onload = (ev) => setDriverLicensePhoto(ev.target?.result as string);
                              reader.readAsDataURL(file);
                            }
                          }}
                        />
                      </Button>
                    )}
                    {/* Optionally add manual entry fields for license number, expiration, etc. */}
                  </Paper>
                </Grid>
              )}
              {/* Step 3: Checklist, Boat Photo, Fuel, Hour Meter */}
              {activeStep === 2 && (
                <Grid xs={12}>
                  <Typography variant="subtitle1" gutterBottom>
                    Check-In Checklist
                  </Typography>
                  <Paper sx={{ p: 1.5, mb: 1 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={boatInspected}
                          onChange={(e) => setBoatInspected(e.target.checked)}
                          color="primary"
                          disabled={loading}
                        />
                      }
                      label="Boat inspected before departure"
                    />
                    <Typography variant="body2" color="text.secondary" sx={{ ml: 4, mb: 2 }}>
                      Check for any pre-existing damage and document condition
                    </Typography>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={safetyEquipmentVerified}
                          onChange={(e) => setSafetyEquipmentVerified(e.target.checked)}
                          color="primary"
                          disabled={loading}
                        />
                      }
                      label="Safety equipment verified"
                    />
                    <Typography variant="body2" color="text.secondary" sx={{ ml: 4, mb: 2 }}>
                      Life jackets, fire extinguisher, horn, etc.
                    </Typography>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={driverLicenseVerified}
                          onChange={(e) => setDriverLicenseVerified(e.target.checked)}
                          color="primary"
                          disabled={loading}
                        />
                      }
                      label="Driver's license verified"
                    />
                    <Typography variant="body2" color="text.secondary" sx={{ ml: 4, mb: 2 }}>
                      Confirm customer has valid driver's license
                    </Typography>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={waiverSigned}
                          onChange={(e) => setWaiverSigned(e.target.checked)}
                          color="primary"
                          disabled={loading}
                        />
                      }
                      label="Liability waiver signed"
                    />
                    <Typography variant="body2" color="text.secondary" sx={{ ml: 4, mb: 2 }}>
                      Ensure customer has signed liability waiver
                    </Typography>
                    <Button
                      variant="outlined"
                      startIcon={<AddAPhotoIcon />}
                      fullWidth
                      sx={{ mt: 2 }}
                      disabled={loading}
                    >
                      Take Boat Photo
                    </Button>
                    <Divider sx={{ my: 0.5 }} />
                    <Typography variant="subtitle1" gutterBottom>
                      Starting Fuel Level
                    </Typography>
                    <Slider
                      value={fuelLevel}
                      onChange={(_, val) => setFuelLevel(val as number)}
                      min={0}
                      max={4}
                      step={1}
                      marks={[
                        { value: 0, label: 'Empty' },
                        { value: 1, label: '1/4' },
                        { value: 2, label: '1/2' },
                        { value: 3, label: '3/4' },
                        { value: 4, label: 'Full' },
                      ]}
                      sx={{ mb: 2 }}
                    />
                    <Typography variant="subtitle1" gutterBottom>
                      Hour Meter Start
                    </Typography>
                    <TextField
                      type="number"
                      label="Hour Meter Start"
                      value={hourMeterStart}
                      onChange={e => setHourMeterStart(e.target.value)}
                      fullWidth
                      sx={{ mb: 2 }}
                      inputProps={{ min: 0 }}
                    />
                    <Divider sx={{ my: 0.5 }} />
                    <Typography variant="subtitle1" gutterBottom>
                      Check-In Notes
                    </Typography>
                    <TextField
                      fullWidth
                      multiline
                      rows={2}
                      size="small"
                      placeholder="Add any additional notes about vessel condition, customer requests, etc."
                      value={checkInNotes}
                      onChange={(e) => setCheckInNotes(e.target.value)}
                      disabled={loading}
                    />
                  </Paper>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, justifyContent: 'space-between' }}>
          <Button
            onClick={activeStep === 0 ? () => setCheckInDialogOpen(false) : handleBack}
            color="inherit"
            disabled={loading}
          >
            {activeStep === 0 ? 'Cancel' : 'Back'}
          </Button>
          <Box>
            {activeStep < 2 ? (
              <Button
                onClick={handleNext}
                variant="contained"
                color="primary"
                disabled={loading || (activeStep === 1 && !assignedVesselId)}
              >
                Next
              </Button>
            ) : (
              <Button
                onClick={handleConfirmCheckIn}
                variant="contained"
                color="primary"
                disabled={loading || !boatInspected || !safetyEquipmentVerified || !driverLicenseVerified || !waiverSigned || !assignedVesselId}
                startIcon={loading ? <CircularProgress size={24} /> : null}
              >
                {loading ? 'Processing...' : 'Complete Check-In'}
              </Button>
            )}
          </Box>
        </DialogActions>
      </Dialog>
      {/* Check-out Dialog */}
      <Dialog 
        open={checkOutDialogOpen} 
        onClose={() => !loading && setCheckOutDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Check Out: {selectedReservation?.customerDetails.firstName} {selectedReservation?.customerDetails.lastName}
        </DialogTitle>
        <DialogContent dividers>
          {selectedReservation && (
            <Grid container spacing={3}>
              <Grid xs={12} md={6}>
                <Paper sx={{ p: 1.5, mb: 1 }}>
                  <List dense disablePadding>
                    <ListItem>
                      <Grid container spacing={1} alignItems="center">
                        <Grid xs={5}>
                          <Typography variant="subtitle2">Payment Breakdown</Typography>
                        </Grid>
                        <Grid
                          sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}
                          xs={3}>
                          {selectedReservation.payment && selectedReservation.payment.depositPaid && selectedReservation.payment.remainingAmount > 0 ? (
                            <Chip label="Deposit Paid" color="info" size="small" />
                          ) : selectedReservation.payment && selectedReservation.payment.remainingAmount <= 0 ? (
                            <Chip label="Paid" color="success" size="small" />
                          ) : (
                            <Chip label={selectedReservation.paymentStatus || 'Unpaid'} color="warning" size="small" />
                          )}
                        </Grid>
                        <Grid
                          sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}
                          xs={4}>
                          <TextField
                            label="Payment Auth Code"
                            value={authCode}
                            onChange={e => setAuthCode(e.target.value)}
                            size="small"
                            sx={{ minWidth: 160 }}
                            inputProps={{ maxLength: 32 }}
                          />
                        </Grid>
                        <Grid xs={12}>
                          <Box sx={{ mt: 0.5 }}>
                            <Typography variant="body2">
                              Duration Price: ${
                                selectedReservation.rentalPrice && !isNaN(Number(selectedReservation.rentalPrice))
                                  ? Number(selectedReservation.rentalPrice).toFixed(2)
                                  : '0.00'
                              }
                            </Typography>
                            <Typography variant="body2">
                              Tax: ${
                                selectedReservation.taxAmount && !isNaN(Number(selectedReservation.taxAmount))
                                  ? Number(selectedReservation.taxAmount).toFixed(2)
                                  : '0.00'
                              }
                            </Typography>
                            <Typography variant="body2">
                              Total Price: ${
                                selectedReservation.finalPrice && !isNaN(Number(selectedReservation.finalPrice))
                                  ? Number(selectedReservation.finalPrice).toFixed(2)
                                  : '0.00'
                              }
                            </Typography>
                            <Typography variant="body2">
                              Deposit Paid: ${selectedReservation.payment?.depositAmount ? selectedReservation.payment.depositAmount.toFixed(2) : '0.00'}
                            </Typography>
                            <Typography variant="body2" fontWeight="bold">
                              Balance Due: ${
                                selectedReservation.finalPrice && !isNaN(Number(selectedReservation.finalPrice))
                                  ? (Number(selectedReservation.finalPrice) - (selectedReservation.payment?.depositAmount || 0)).toFixed(2)
                                  : '0.00'
                              }
                            </Typography>
                          </Box>
                        </Grid>
                      </Grid>
                    </ListItem>
                    <Divider component="li" sx={{ my: 0.5 }} />
                    <ListItem>
                      <Grid container spacing={1}>
                        <Grid xs={12} sm={4}>
                          <Typography variant="subtitle2">Vessel Out Time</Typography>
                          <Typography variant="body2">
                            {(() => {
                              try {
                                if (selectedReservation.checkInTime?.toDate) {
                                  // Firebase Timestamp
                                  return format(selectedReservation.checkInTime.toDate(), 'PPpp');
                                } else if (typeof selectedReservation.checkInTime === 'string') {
                                  // ISO string
                                  return format(parseISO(selectedReservation.checkInTime), 'PPpp');
                                }
                                return 'N/A';
                              } catch {
                                return 'N/A';
                              }
                            })()}
                          </Typography>
                        </Grid>
                        <Grid xs={12} sm={4}>
                          <Typography variant="subtitle2">Starting Fuel Level</Typography>
                          <Typography variant="body2">
                            {typeof selectedReservation.startingFuelLevel === 'number' ? ['Empty', '1/4', '1/2', '3/4', 'Full'][selectedReservation.startingFuelLevel] : 'N/A'}
                          </Typography>
                        </Grid>
                        <Grid xs={12} sm={4}>
                          <Typography variant="subtitle2">Hour Meter Start</Typography>
                          <Typography variant="body2">
                            {selectedReservation.hourMeterStart ?? 'N/A'}
                          </Typography>
                        </Grid>
                      </Grid>
                    </ListItem>
                    <Divider component="li" sx={{ my: 0.5 }} />
                    <ListItem>
                      <Grid container spacing={1}>
                        <Grid xs={12} sm={6}>
                          <Typography variant="subtitle2">Fuel Gallons Used</Typography>
                          <TextField
                            type="number"
                            label="Fuel Gallons Used"
                            value={fuelGallonsUsed}
                            onChange={e => setFuelGallonsUsed(e.target.value)}
                            fullWidth
                            sx={{ mt: 1, mb: 1 }}
                            inputProps={{ min: 0, step: 0.01 }}
                          />
                        </Grid>
                        <Grid xs={12} sm={6}>
                          <Typography variant="subtitle2">Return Hour Meter</Typography>
                          <TextField
                            type="number"
                            label="Return Hour Meter"
                            value={returnHourMeter}
                            onChange={e => setReturnHourMeter(e.target.value)}
                            fullWidth
                            sx={{ mt: 1, mb: 1 }}
                            inputProps={{ min: 0, step: 0.01 }}
                          />
                        </Grid>
                      </Grid>
                    </ListItem>
                  </List>
                </Paper>
                
                <Typography variant="subtitle1" gutterBottom>Vessel Return Details</Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  size="small"
                  placeholder="Add notes about vessel condition, fuel level, any damages, etc."
                  value={checkOutNotes}
                  onChange={(e) => setCheckOutNotes(e.target.value)}
                  disabled={loading}
                  sx={{ mb: 2 }}
                />
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2">Damage Photo</Typography>
                  {damagePhoto || damagePhotoUrl ? (
                    <Box sx={{ mb: 1 }}>
                      <img
                        src={damagePhotoUrl || damagePhoto!}
                        alt="Damage Photo"
                        style={{ maxWidth: 180, borderRadius: 4, border: '1px solid #ccc' }}
                      />
                    </Box>
                  ) : null}
                  <Button
                    variant="outlined"
                    startIcon={<AddAPhotoIcon />}
                    component="label"
                    sx={{ mb: 2 }}
                    disabled={uploadingDamagePhoto}
                  >
                    {uploadingDamagePhoto ? 'Uploading...' : 'Upload Damage Photo'}
                    <input
                      type="file"
                      accept="image/*"
                      hidden
                      onChange={e => {
                        const file = e.target.files?.[0];
                        if (file) {
                          const reader = new FileReader();
                          reader.onload = (ev) => setDamagePhoto(ev.target?.result as string);
                          reader.readAsDataURL(file);
                        }
                      }}
                    />
                  </Button>
                </Box>
              </Grid>
              
              {renderPaymentSection()}
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, justifyContent: 'space-between' }}>
          <Button
            onClick={() => setCheckOutDialogOpen(false)}
            color="inherit"
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmCheckOut}
            variant="contained"
            color="success"
            disabled={loading || !authCode || fuelGallonsUsed === '' || (remainingBalance > 0 && !paymentComplete)}
            startIcon={loading ? <CircularProgress size={24} /> : null}
          >
            {loading ? 'Processing...' : (remainingBalance > 0 && (!paymentComplete || !authCode) ? 'Payment Required' : 'Complete Check-Out')}
          </Button>
        </DialogActions>
      </Dialog>
      {/* Snackbar notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbarOpen(false)} 
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CheckInPage; 